import { defaultRulePattern } from '@budgie/shared';
import { createStore, type StoreApi } from 'zustand/vanilla';
import { applyRuleToImportBatch, type TxnOverride } from '../../routes/import/-rule-application';

export type AccountProposal = {
  accountNumber: string;
  existingAccountId: string | null;
  suggestedName: string | null;
  transactionCount: number;
};

export type ImportedTxn = {
  id: string;
  date: string;
  description: string;
  amount: number;
  categoryId: string | null;
  isDuplicate: boolean;
  isTransfer: boolean;
  confidence: number;
  matchedRuleId: string | null;
  autoConfirmed: boolean;
  accountNumber: string;
};

export type AccountOverride = {
  approve: boolean;
  friendlyName: string;
  memberId: string;
};

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export type RuleApplicationNotice = {
  pattern: string;
  appliedCount: number;
};

export interface PersistedReviewStateV1 {
  version: 1;
  transactionOverrides: Record<string, TxnOverride>;
  accountOverrides: Record<string, AccountOverride>;
}

export interface InitializeImportDraftInput {
  batchId: string;
  transactions: ImportedTxn[];
  accountProposals: AccountProposal[];
  skippedReservedCount?: number;
}

export interface HydrateSavedImportDraftInput extends InitializeImportDraftInput {
  reviewState: PersistedReviewStateV1;
}

export type RuleSummary = {
  pattern: string;
  categoryLabel: string;
  confidence: number;
};

export interface ImportReviewStoreState {
  batchId: string | null;
  transactions: ImportedTxn[];
  accountProposals: AccountProposal[];
  overrides: Record<string, TxnOverride>;
  accountOverrides: Record<string, AccountOverride>;
  skippedReservedCount: number;
  saveStatus: SaveStatus;
  lastSavedAt: string | null;
  isResuming: boolean;
  ruleApplicationNotice: RuleApplicationNotice | null;
}

export interface ImportReviewStoreActions {
  initializeFromImport(input: InitializeImportDraftInput): void;
  hydrateSavedProgress(input: HydrateSavedImportDraftInput): void;
  setTransactionOverride(id: string, override: TxnOverride): void;
  setAccountOverride(accountNumber: string, override: AccountOverride): void;
  setIsResuming(isResuming: boolean): void;
  clearRuleApplicationNotice(): void;
  markSaving(): void;
  markSaved(savedAt: string): void;
  markSaveError(): void;
  resetWorkflow(): void;
}

export type ImportReviewStore = ImportReviewStoreState & ImportReviewStoreActions;
export type ImportReviewStoreApi = StoreApi<ImportReviewStore>;

const initialState: ImportReviewStoreState = {
  batchId: null,
  transactions: [],
  accountProposals: [],
  overrides: {},
  accountOverrides: {},
  skippedReservedCount: 0,
  saveStatus: 'idle',
  lastSavedAt: null,
  isResuming: false,
  ruleApplicationNotice: null,
};

export function createInitialTransactionOverride(transaction: ImportedTxn): TxnOverride {
  return {
    categoryId: transaction.categoryId,
    skip: transaction.isDuplicate,
    createRule: false,
    isTransfer: transaction.isTransfer,
    createTransferRule: false,
    rulePattern: null,
    isManualOverride: false,
    autoAppliedRulePattern: null,
  };
}

export function createInitialTransactionOverrides(
  transactions: ImportedTxn[],
): Record<string, TxnOverride> {
  return Object.fromEntries(
    transactions.map((transaction) => [
      transaction.id,
      createInitialTransactionOverride(transaction),
    ]),
  );
}

export function createInitialAccountOverride(proposal: AccountProposal): AccountOverride {
  return {
    approve: !proposal.existingAccountId,
    friendlyName: proposal.suggestedName ?? '',
    memberId: '',
  };
}

export function createInitialAccountOverrides(
  proposals: AccountProposal[],
): Record<string, AccountOverride> {
  return Object.fromEntries(
    proposals.map((proposal) => [proposal.accountNumber, createInitialAccountOverride(proposal)]),
  );
}

function markDraftDirty(saveStatus: SaveStatus): SaveStatus {
  return saveStatus === 'saving' ? saveStatus : 'idle';
}

export function createImportReviewStore(): ImportReviewStoreApi {
  return createStore<ImportReviewStore>()((set, get) => ({
    ...initialState,

    initializeFromImport(input) {
      set({
        batchId: input.batchId,
        transactions: input.transactions,
        accountProposals: input.accountProposals,
        overrides: createInitialTransactionOverrides(input.transactions),
        accountOverrides: createInitialAccountOverrides(input.accountProposals),
        skippedReservedCount: input.skippedReservedCount ?? 0,
        saveStatus: 'idle',
        lastSavedAt: null,
        isResuming: false,
        ruleApplicationNotice: null,
      });
    },

    hydrateSavedProgress(input) {
      set({
        batchId: input.batchId,
        transactions: input.transactions,
        accountProposals: input.accountProposals,
        overrides: {
          ...createInitialTransactionOverrides(input.transactions),
          ...input.reviewState.transactionOverrides,
        },
        accountOverrides: {
          ...createInitialAccountOverrides(input.accountProposals),
          ...input.reviewState.accountOverrides,
        },
        skippedReservedCount: input.skippedReservedCount ?? 0,
        saveStatus: 'idle',
        lastSavedAt: null,
        isResuming: false,
        ruleApplicationNotice: null,
      });
    },

    setTransactionOverride(id, override) {
      const transactions = get().transactions;

      set((state) => {
        const nextOverrides = {
          ...state.overrides,
          [id]: override,
        };
        const previousOverride = state.overrides[id];
        let ruleApplicationNotice = state.ruleApplicationNotice;

        const transferRuleChanged =
          override.createTransferRule !== previousOverride?.createTransferRule;
        const transferChanged = override.isTransfer !== previousOverride?.isTransfer;

        if (transferRuleChanged || transferChanged) {
          const description = transactions.find(
            (transaction) => transaction.id === id,
          )?.description;
          if (description) {
            for (const transaction of transactions) {
              if (transaction.id === id || transaction.description !== description) continue;
              const existing = nextOverrides[transaction.id] ?? state.overrides[transaction.id];
              if (!existing || existing.skip) continue;
              nextOverrides[transaction.id] = {
                ...existing,
                isTransfer: override.isTransfer,
                createTransferRule: override.createTransferRule,
                createRule: override.isTransfer ? false : existing.createRule,
                rulePattern: override.isTransfer ? null : existing.rulePattern,
                autoAppliedRulePattern: override.isTransfer
                  ? null
                  : existing.autoAppliedRulePattern,
              };
            }
          }
        }

        const shouldApplyRule =
          override.createRule &&
          !!override.categoryId &&
          !!override.rulePattern &&
          (!previousOverride?.createRule ||
            previousOverride.categoryId !== override.categoryId ||
            previousOverride.rulePattern !== override.rulePattern);

        if (shouldApplyRule && override.categoryId && override.rulePattern) {
          const result = applyRuleToImportBatch({
            sourceTransactionId: id,
            transactions,
            overrides: nextOverrides,
            pattern: override.rulePattern,
            categoryId: override.categoryId,
          });

          if (result.appliedCount > 0) {
            ruleApplicationNotice = {
              pattern: override.rulePattern,
              appliedCount: result.appliedCount,
            };
          }

          return {
            overrides: result.overrides,
            ruleApplicationNotice,
            saveStatus: markDraftDirty(state.saveStatus),
          };
        }

        return {
          overrides: nextOverrides,
          ruleApplicationNotice,
          saveStatus: markDraftDirty(state.saveStatus),
        };
      });
    },

    setAccountOverride(accountNumber, override) {
      set((state) => ({
        accountOverrides: {
          ...state.accountOverrides,
          [accountNumber]: override,
        },
        saveStatus: markDraftDirty(state.saveStatus),
      }));
    },

    setIsResuming(isResuming) {
      set({ isResuming });
    },

    clearRuleApplicationNotice() {
      set({ ruleApplicationNotice: null });
    },

    markSaving() {
      set({ saveStatus: 'saving' });
    },

    markSaved(savedAt) {
      set({
        saveStatus: 'saved',
        lastSavedAt: savedAt,
      });
    },

    markSaveError() {
      set({ saveStatus: 'error' });
    },

    resetWorkflow() {
      set(initialState);
    },
  }));
}

export function selectPersistedReviewState(
  state: Pick<ImportReviewStoreState, 'overrides' | 'accountOverrides'>,
): PersistedReviewStateV1 {
  return {
    version: 1,
    transactionOverrides: state.overrides,
    accountOverrides: state.accountOverrides,
  };
}

export function buildConfirmImportInput(
  state: Pick<
    ImportReviewStoreState,
    'batchId' | 'accountProposals' | 'accountOverrides' | 'transactions' | 'overrides'
  >,
) {
  if (!state.batchId) {
    return null;
  }

  return {
    batchId: state.batchId,
    accounts: state.accountProposals
      .filter((proposal) => !proposal.existingAccountId)
      .map((proposal) => ({
        accountNumber: proposal.accountNumber,
        approve: state.accountOverrides[proposal.accountNumber]?.approve ?? false,
        friendlyName: state.accountOverrides[proposal.accountNumber]?.friendlyName || null,
        memberId: state.accountOverrides[proposal.accountNumber]?.memberId || null,
      })),
    transactions: state.transactions.map((transaction) => ({
      id: transaction.id,
      categoryId: state.overrides[transaction.id]?.categoryId ?? null,
      skip: state.overrides[transaction.id]?.skip ?? false,
      createRule: state.overrides[transaction.id]?.createRule ?? false,
      isTransfer: state.overrides[transaction.id]?.isTransfer ?? false,
      createTransferRule: state.overrides[transaction.id]?.createTransferRule ?? false,
      rulePattern: state.overrides[transaction.id]?.rulePattern ?? null,
    })),
  };
}

export function buildRuleSummary(args: {
  transactions: ImportedTxn[];
  overrides: Record<string, TxnOverride>;
  getCategoryLabel: (id: string) => string;
}): RuleSummary[] {
  const summary = args.transactions
    .filter(
      (transaction) =>
        args.overrides[transaction.id]?.createRule &&
        !args.overrides[transaction.id]?.skip &&
        args.overrides[transaction.id]?.categoryId,
    )
    .reduce((accumulator, transaction) => {
      const key =
        args.overrides[transaction.id]?.rulePattern ?? defaultRulePattern(transaction.description);
      if (!accumulator.has(key)) {
        accumulator.set(key, {
          pattern: key,
          categoryLabel: args.getCategoryLabel(args.overrides[transaction.id]?.categoryId ?? ''),
          confidence: 80,
        });
      }
      return accumulator;
    }, new Map<string, RuleSummary>());

  return Array.from(summary.values());
}
